/**
 * Authentication context for the app.
 *
 * Wraps Supabase Auth and exposes the current user, session, loading state,
 * and sign-up/in/out methods to the rest of the component tree.
 *
 * Supabase persists the session in localStorage automatically, so on refresh
 * the user stays signed in without any additional storage logic here.
 */
import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { User, Session } from '@supabase/supabase-js';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import { getAppUrl } from '@/lib/constants';
import posthog from '@/lib/posthog';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (
    email: string,
    password: string,
    displayName?: string,
  ) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 *
 * @param root0
 * @param root0.children
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Register the auth state listener BEFORE calling getSession so there is
    // no window where a sign-in event could fire and be missed. Supabase
    // recommends this ordering to avoid race conditions on page load.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Identify or reset PostHog user on auth state change.
      // Only the opaque user ID and email address are sent; no other PII.
      // Email is included here so PostHog can de-duplicate returning users
      // across devices — it is not forwarded to any other analytics sink.
      if (session?.user) {
        posthog.identify(session.user.id, { email: session.user.email });
        Sentry.setUser({ id: session.user.id, email: session.user.email });
      } else {
        posthog.reset();
        Sentry.setUser(null);
      }
    });

    // Hydrate the initial session state (e.g. on page refresh).
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  /**
   * Creates a new account. The `emailRedirectTo` URL is where Supabase
   * redirects the user after clicking the confirmation link in their email.
   * `display_name` is stored in Supabase's `user_metadata` and copied to the
   * `profiles` table by a DB trigger on first sign-in.
   * @param email
   * @param password
   * @param displayName
   */
  const signUp = async (email: string, password: string, displayName?: string) => {
    const redirectUrl = `${getAppUrl()}/`;

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          display_name: displayName,
        },
      },
    });

    return { error: error as Error | null };
  };

  /**
   * Signs in with email and password. Returns `{ error: null }` on success.
   * @param email
   * @param password
   */
  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    return { error: error as Error | null };
  };

  /**
   * Signs the user out and revokes all active sessions across every device.
   *
   * `scope: 'global'` invalidates every refresh token the user holds, not just
   * the current browser session. This is intentional: if a user signs out they
   * likely want a clean break (e.g. on a shared device), and it eliminates the
   * risk of stale tokens being replayed from other tabs or devices.
   */
  const signOut = async () => {
    // Remove ALL device tokens for this user before signing out.
    // Intentionally deletes tokens across all devices — scope: 'global' revokes
    // every session, so push tokens on other devices would become orphaned anyway.
    // If single-device sign-out is added later, switch to deleting only the
    // current device's token.
    if (Capacitor.isNativePlatform() && user) {
      try {
        await supabase.from('device_tokens').delete().eq('user_id', user.id);
      } catch {
        // Non-blocking — sign out regardless
      }
    }
    await supabase.auth.signOut({ scope: 'global' });
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to access auth state and methods from any component inside AuthProvider.
 * Throws if called outside of the provider tree to catch integration mistakes early.
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
